import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Phone, X, DoorOpen, Volume2, VolumeX, Loader2, CheckCircle } from 'lucide-react';
import useIntercomStore from '../stores/intercomStore';
import useIntercomCallStore from '../stores/intercomCallStore';
import WebRTCPlayer from './WebRTCPlayer';
import useStore from '../stores/useStore';
import { fetchWithTenant } from '../utils/fetchWithTenant';

export function IntercomNotificationToast() {
  const { notifications, removeNotification, callIdMap, clearCallId } = useIntercomStore();
  const [, setTick] = useState(0);
  const [mutedStates, setMutedStates] = useState({});
  const [isUnlocking, setIsUnlocking] = useState({});
  const [unlockSuccess, setUnlockSuccess] = useState({});
  const [isDismissing, setIsDismissing] = useState({});
  const { waveConfig, setWaveConfig } = useStore();
  const { callState, answerCall } = useIntercomCallStore();

  const handleAnswerCall = async (notification) => {
    await acknowledgeCall(notification, 'answer');
    const tenantId = document.cookie.match(/tenantId=([^;]+)/)?.[1] || 'default';
    const success = await answerCall(
      notification.intercomId || notification.id,
      notification.cameraId,
      notification.intercomName || notification.title || 'Intercom',
      tenantId
    );
    if (!success) {
      console.error('Failed to start audio call');
    }
  };

  const pendingNotifications = notifications.filter(n => !n.answered);

  // Auto-load waveConfig if not yet populated
  useEffect(() => {
    if (!waveConfig?.host && pendingNotifications.length > 0) {
      fetchWithTenant('/api/tenant/config').then(r => r.json()).then(config => {
        if (config.waveConfig) setWaveConfig(config.waveConfig);
      }).catch(() => {});
    }
  }, [pendingNotifications.length, waveConfig?.host]);

  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Initialize muted state for new notifications - DEFAULT TO UNMUTED (audio on)
  useEffect(() => {
    pendingNotifications.forEach(n => {
      if (mutedStates[n.id] === undefined) {
        setMutedStates(prev => ({ ...prev, [n.id]: false })); // false = unmuted = audio ON
      }
    });
  }, [pendingNotifications]);

  if (pendingNotifications.length === 0) return null;

  // Acknowledge call via API (stops persistent notifications)
  const acknowledgeCall = async (notification, action) => {
    const callId = callIdMap[notification.intercomId];
    if (callId) {
      try {
        await fetch(`/api/intercom/calls/${callId}/acknowledge`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action })
        });
        console.log(`[Intercom] Call ${callId} acknowledged: ${action}`);
        clearCallId(notification.intercomId);
      } catch (error) {
        console.error('[Intercom] Failed to acknowledge call:', error);
      }
    }
  };

  const handleDismiss = async (notification) => {
    setIsDismissing(prev => ({ ...prev, [notification.id]: true }));
    
    // Acknowledge the call to stop persistent notifications
    await acknowledgeCall(notification, 'dismiss');
    
    // Remove from local state
    removeNotification(notification.id);
    setMutedStates(prev => { const next = {...prev}; delete next[notification.id]; return next; });
    setIsDismissing(prev => { const next = {...prev}; delete next[notification.id]; return next; });
  };

  const handleAnswer = async (notification) => {
    // Acknowledge the call as answered
    await acknowledgeCall(notification, 'answer');
  };

  const handleQuickUnlock = async (notification) => {
    setIsUnlocking(prev => ({ ...prev, [notification.id]: true }));
    
    // Also acknowledge the call when unlocking (user is responding)
    await acknowledgeCall(notification, 'answer');
    
    try {
      const response = await fetch(`/api/intercom/${notification.intercomId}/unlock`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setUnlockSuccess(prev => ({ ...prev, [notification.id]: true }));
        setTimeout(() => {
          setUnlockSuccess(prev => ({ ...prev, [notification.id]: false }));
          // Remove notification after successful unlock
          removeNotification(notification.id);
        }, 2000);
      }
    } catch (error) {
      console.error('Unlock failed:', error);
    } finally {
      setIsUnlocking(prev => ({ ...prev, [notification.id]: false }));
    }
  };

  const toggleMute = (notificationId, e) => {
    e.stopPropagation();
    setMutedStates(prev => ({ ...prev, [notificationId]: !prev[notificationId] }));
  };

  const getTimeSince = (timestamp) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 10) return 'Just now';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  };

  const notification = pendingNotifications[0];

  // Create camera object matching how Intercoms.jsx does it
  const cameraObj = notification.cameraId ? {
    id: notification.cameraId,
    name: notification.intercomName
  } : null;

  const toastContent = (
    <>
      {/* Dark backdrop */}
      <div
        className="intercom-toast-backdrop"
        onClick={() => handleDismiss(notification)}
      />

      {/* Modal */}
      <div className="intercom-toast-modal">
        <div className="intercom-toast-card">
          {/* Animated top bar */}
          <div className="intercom-toast-topbar" />
          {/* Timeout progress bar */}
          <div className="intercom-toast-progress" />

          {/* Header */}
          <div className="intercom-toast-header">
            <div className="intercom-toast-header-left">
              <div className="intercom-toast-icon">
                <Phone size={24} />
                <span className="intercom-toast-pulse" />
              </div>
              <div>
                <h3 className="intercom-toast-title">Incoming Call</h3>
                <p className="intercom-toast-subtitle">{notification.intercomName}</p>
              </div>
            </div>
            <button 
              className="btn btn-icon" 
              onClick={() => handleDismiss(notification)}
              disabled={isDismissing[notification.id]}
            >
              <X size={20} />
            </button>
          </div>

          {/* Video area */}
          <div className="intercom-toast-video-container">
            {cameraObj ? (
              <>
                <WebRTCPlayer
                  camera={cameraObj}
                  stream="primary"
                  muted={mutedStates[notification.id] === true}
                />
                <div className="intercom-toast-live-badge">
                  <span className="intercom-toast-live-dot" />
                  LIVE
                </div>
                <button
                  className="intercom-toast-mute-btn"
                  onClick={(e) => toggleMute(notification.id, e)}
                >
                  {mutedStates[notification.id] === true ? (
                    <VolumeX size={18} />
                  ) : (
                    <Volume2 size={18} />
                  )}
                </button>
              </>
            ) : (
              <div className="intercom-toast-no-camera">
                No camera available
              </div>
            )}
          </div>

          {/* Info row */}
          <div className="intercom-toast-info">
            <span>{getTimeSince(notification.timestamp)}</span>
            <span className="intercom-toast-badge">{notification.brand || 'Intercom'}</span>
          </div>

          {/* Answer & Unlock Buttons */}
          <div className="intercom-toast-buttons">
            <button
              className={`btn btn-answer ${callState === 'active' ? 'btn-success' : ''}`}
              onClick={() => handleAnswerCall(notification)}
              disabled={callState === 'active' || callState === 'connecting'}
            >
              <Phone size={18} />
              {callState === 'connecting' ? 'Connecting...' : callState === 'active' ? 'On Call' : 'Answer'}
            </button>
            <button
              className={`btn ${unlockSuccess[notification.id] ? 'btn-success' : 'btn-primary'}`}
              onClick={() => handleQuickUnlock(notification)}
              disabled={isUnlocking[notification.id]}
            >
              {isUnlocking[notification.id] ? (
                <Loader2 size={18} className="spin" />
              ) : unlockSuccess[notification.id] ? (
                <CheckCircle size={18} />
              ) : (
                <DoorOpen size={18} />
              )}
              {unlockSuccess[notification.id] ? 'Unlocked!' : 'Unlock Door'}
            </button>
          </div>

          <button
            className="intercom-toast-dismiss"
            onClick={() => handleDismiss(notification)}
            disabled={isDismissing[notification.id]}
          >
            {isDismissing[notification.id] ? 'Dismissing...' : 'Dismiss'}
          </button>

          {/* More calls indicator */}
          {pendingNotifications.length > 1 && (
            <div className="intercom-toast-more">
              +{pendingNotifications.length - 1} more call{pendingNotifications.length > 2 ? 's' : ''}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(toastContent, document.body);
}

export default IntercomNotificationToast;
